import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Phone,
  FileText,
  Calendar,
  MessageSquare,
  PenTool,
  CheckSquare,
  StickyNote,
  Receipt,
  Send,
  ChevronDown,
  ChevronRight,
  Activity,
  Eye,
  Download,
  Clock,
  Users,
  Loader2,
} from "lucide-react";
import { activityLogsApi } from "../../services/api";

// ─── Types ──────────────────────────────────────────────────────────────────

interface ActivityItem {
  _id: string;
  action: string;
  description: string;
  occurredAt: string;
  durationSecs?: number | null;
  matterId?: { _id: string; title?: string; matterNumber?: string } | null;
  clientId?: { _id: string; displayName?: string } | null;
  entityType: string;
}

interface Props {
  onCreateFromActivity?: (activity: ActivityItem) => void;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const ACTION_ICONS: Record<string, React.ElementType> = {
  message_sent: MessageSquare,
  message_received: MessageSquare,
  email_sent: Send,
  email_received: Mail,
  call_completed: Phone,
  call_missed: Phone,
  document_uploaded: FileText,
  document_created: FileText,
  document_viewed: Eye,
  document_downloaded: Download,
  esign_sent: PenTool,
  esign_completed: PenTool,
  calendar_event_created: Calendar,
  calendar_event_attended: Calendar,
  task_completed: CheckSquare,
  note_created: StickyNote,
  invoice_created: Receipt,
  invoice_sent: Receipt,
  matter_updated: FileText,
  time_entry_created: Clock,
  client_communication: Users,
};

const ACTION_COLORS: Record<string, string> = {
  email_sent: "text-blue-500 bg-blue-50",
  email_received: "text-blue-500 bg-blue-50",
  message_sent: "text-purple-500 bg-purple-50",
  message_received: "text-purple-500 bg-purple-50",
  call_completed: "text-emerald-600 bg-emerald-50",
  call_missed: "text-red-500 bg-red-50",
  document_uploaded: "text-amber-600 bg-amber-50",
  document_created: "text-amber-600 bg-amber-50",
  document_viewed: "text-slate-500 bg-slate-50",
  document_downloaded: "text-slate-500 bg-slate-50",
  esign_sent: "text-indigo-500 bg-indigo-50",
  esign_completed: "text-indigo-500 bg-indigo-50",
  calendar_event_created: "text-teal-500 bg-teal-50",
  calendar_event_attended: "text-teal-500 bg-teal-50",
  task_completed: "text-emerald-600 bg-emerald-50",
  note_created: "text-yellow-600 bg-yellow-50",
  invoice_created: "text-emerald-600 bg-emerald-50",
  invoice_sent: "text-emerald-600 bg-emerald-50",
  matter_updated: "text-slate-500 bg-slate-100",
  time_entry_created: "text-emerald-600 bg-emerald-50",
  client_communication: "text-blue-500 bg-blue-50",
};

function formatTime(dateStr: string) {
  return new Date(dateStr).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

function formatDateLabel(dateStr: string): string {
  const date = new Date(dateStr + "T00:00:00");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);

  if (d.getTime() === today.getTime()) return "Today";
  if (d.getTime() === yesterday.getTime()) return "Yesterday";
  return d.toLocaleDateString("en-CA", {
    weekday: "long",
    month: "long",
    day: "numeric",
  });
}

// ─── Component ──────────────────────────────────────────────────────────────

const ActivityTimeline: React.FC<Props> = ({ onCreateFromActivity }) => {
  const [grouped, setGrouped] = useState<Record<string, ActivityItem[]>>({});
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [expandedDates, setExpandedDates] = useState<Set<string>>(new Set());
  const [totalCount, setTotalCount] = useState(0);

  const fetchActivities = useCallback(async () => {
    try {
      setLoading(true);
      const res = await activityLogsApi.mine(7);
      const data = res.data;
      setGrouped(data.grouped || {});
      setTotalCount(data.totalCount || 0);

      // Auto-expand today
      const today = new Date().toISOString().split("T")[0];
      if (data.grouped?.[today]) {
        setExpandedDates(new Set([today]));
      }
    } catch (err) {
      console.error("Failed to load activities:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const toggleDate = (date: string) => {
    setExpandedDates((prev) => {
      const next = new Set(prev);
      if (next.has(date)) next.delete(date);
      else next.add(date);
      return next;
    });
  };

  const sortedDates = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

  // Today's count for the summary
  const today = new Date().toISOString().split("T")[0];
  const todayCount = grouped[today]?.length || 0;

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
        <div className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" />
          Loading activity...
        </div>
      </div>
    );
  }

  if (totalCount === 0) return null;

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
      {/* Collapsed summary bar */}
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full px-5 py-3.5 flex items-center justify-between hover:bg-slate-50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-slate-100 flex items-center justify-center">
            <Activity className="w-4 h-4 text-slate-500" />
          </div>
          <div className="text-left">
            <p className="text-sm font-medium text-slate-700">
              {todayCount > 0
                ? `${todayCount} activit${todayCount === 1 ? "y" : "ies"} captured today`
                : `${totalCount} activit${totalCount === 1 ? "y" : "ies"} this week`}
            </p>
            <p className="text-xs text-slate-400">Auto-tracked from your work</p>
          </div>
        </div>
        <ChevronDown
          className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? "rotate-180" : ""}`}
        />
      </button>

      {/* Expanded content */}
      <AnimatePresence>
        {expanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="border-t border-slate-100"
          >
            <div className="max-h-[400px] overflow-y-auto">
              {sortedDates.map((dateKey) => {
                const items = grouped[dateKey] || [];
                const isExpanded = expandedDates.has(dateKey);

                return (
                  <div key={dateKey} className="border-b border-slate-50 last:border-b-0">
                    {/* Date header */}
                    <button
                      onClick={() => toggleDate(dateKey)}
                      className="w-full px-5 py-2.5 flex items-center justify-between bg-slate-50/50 hover:bg-slate-50 transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <ChevronRight
                          className={`w-3.5 h-3.5 text-slate-400 transition-transform ${
                            isExpanded ? "rotate-90" : ""
                          }`}
                        />
                        <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                          {formatDateLabel(dateKey)}
                        </span>
                      </div>
                      <span className="text-xs text-slate-400 font-medium">
                        {items.length} activit{items.length === 1 ? "y" : "ies"}
                      </span>
                    </button>

                    {/* Activities for this date */}
                    <AnimatePresence>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.15 }}
                        >
                          {items.map((act, idx) => {
                            const IconComponent = ACTION_ICONS[act.action] || Activity;
                            const colorClass = ACTION_COLORS[act.action] || "text-slate-500 bg-slate-50";

                            return (
                              <div
                                key={act._id}
                                className="px-5 py-2.5 flex items-center gap-3 hover:bg-slate-50 transition-colors group"
                              >
                                {/* Icon */}
                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${colorClass}`}>
                                  <IconComponent className="w-3.5 h-3.5" />
                                </div>

                                {/* Description */}
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm text-slate-700 truncate">
                                    {act.description}
                                  </p>
                                  <div className="flex items-center gap-2 mt-0.5">
                                    <span className="text-[11px] text-slate-400">
                                      {formatTime(act.occurredAt)}
                                    </span>
                                    {act.matterId?.title && (
                                      <span className="text-[11px] text-slate-400 truncate">
                                        {act.matterId.matterNumber && `${act.matterId.matterNumber} — `}
                                        {act.matterId.title}
                                      </span>
                                    )}
                                  </div>
                                </div>

                                {/* Duration if available */}
                                {act.durationSecs && act.durationSecs > 0 && (
                                  <span className="text-[11px] text-slate-400 flex-shrink-0">
                                    {Math.ceil(act.durationSecs / 60)}m
                                  </span>
                                )}

                                {/* Create time entry button */}
                                {onCreateFromActivity && act.matterId && (
                                  <button
                                    onClick={() => onCreateFromActivity(act)}
                                    className="opacity-0 group-hover:opacity-100 text-[11px] font-medium text-emerald-600 hover:text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md transition-all flex-shrink-0"
                                  >
                                    + Time entry
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                );
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActivityTimeline;
